import React, { useState } from 'react';
import { useAuth } from '@/lib';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import {
  Settings,
  Logout,
  Notifications,
  Help,
  Refresh,
  BugReport,
  SentimentSatisfiedAlt,
} from '@mui/icons-material';
import { isDevelopment, shouldUseMockData } from '../config/dev';
import { invalidateBusinessIdeaCache } from '@/lib/cache';
import { useBusinessIdea } from '../hooks/useBusinessIdea';

export const UserAvatar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleClose();
  };

  if (!user) {
    return null;
  }

  const getUserInitials = () => {
    if (user.displayName) {
      return user.displayName
        .split(' ')
        .map((name) => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          p: 0,
          '&:hover': {
            opacity: 0.8,
          },
        }}
      >
        <Avatar
          src={user.photoURL || undefined}
          alt={user.displayName || user.email || 'User'}
          sx={{
            width: 36,
            height: 36,
            bgcolor: 'primary.main',
            border: '2px solid',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          {getUserInitials() || <SentimentSatisfiedAlt sx={{ fontSize: 20, color: 'white' }} />}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 240,
            mt: 1,
            bgcolor: 'background.surface',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Header */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {user.displayName || 'User'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            {user.email}
          </Typography>
        </Box>

        <Divider />

        {/* Menu Items */}
        <MenuItem onClick={() => handleNavigation('/settings')}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </MenuItem>

        <MenuItem onClick={() => handleNavigation('/notifications')}>
          <ListItemIcon>
            <Notifications fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Notifications" />
        </MenuItem>

        <MenuItem onClick={() => handleNavigation('/help')}>
          <ListItemIcon>
            <Help fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Help & Support" />
        </MenuItem>

        {/* Dev-Only Section */}
        {isDevelopment() && (
          <>
            {/* Cache Reset Button */}
            <MenuItem
              onClick={() => {
                if (user) {
                  invalidateBusinessIdeaCache(user.id);
                  // You can add refetch logic here if needed
                }
                handleClose();
              }}
            >
              <ListItemIcon>
                <Refresh fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Clear Cache" />
            </MenuItem>

            {/* Mock Data Toggle Info */}
            {shouldUseMockData() && (
              <MenuItem
                disabled
                sx={{
                  color: 'text.secondary',
                  cursor: 'default',
                  backgroundColor: 'action.hover',
                  fontSize: '0.875rem',
                  fontStyle: 'italic',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  '&.Mui-disabled': {
                    opacity: 1,
                    color: 'text.secondary',
                  },
                }}
              >
                <ListItemIcon>
                  <BugReport fontSize="small" sx={{ color: 'info.main' }} />
                </ListItemIcon>
                <ListItemText primary="Using Mock Data" />
              </MenuItem>
            )}
          </>
        )}

        <Divider />

        {/* Logout */}
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText primary="Sign Out" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserAvatar;
